/** Global popup dialogs. */

const DIALOG_SELECTOR = 'dialog[data-dialog]';
const TRIGGER_SELECTOR = '[data-dialog-trigger][aria-controls]';
const CLOSE_SELECTOR = '[data-dialog-close]';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');
const SCROLL_LOCK_ATTRIBUTE = 'data-dialog-open';
const INLINE_ATTRIBUTE = 'data-dialog-inline';
const GUARDED_GESTURES = [
  'wheel',
  'touchstart',
  'touchmove',
  'touchend',
  'touchcancel',
  'pointerdown',
  'pointermove',
  'pointerup',
  'pointercancel',
  'mousedown',
  'mousemove',
  'mouseup',
];
const CANCELLED_GESTURES = new Set(['wheel', 'touchmove']);

const openerByDialog = new WeakMap();
const skipFocusRestore = new WeakSet();

let gestureGuardActive = false;
let gestureStartedInDialog = false;
let cachedDialogRects = null;

function isManagedDialog(node) {
  return node instanceof HTMLDialogElement && node.matches(DIALOG_SELECTOR);
}

/**
 * Whether the theme editor is rendering this document.
 *
 * @returns {boolean} True in Shopify design mode.
 */
function isDesignMode() {
  return Boolean(window.Shopify && window.Shopify.designMode);
}

/**
 * Derive the lock from the DOM rather than a counter, so a dialog removed while
 * open — a replaced section, a discarded editor block — cannot strand the lock.
 */
function updateScrollLock() {
  const hasOpenDialog = document.querySelector(`${DIALOG_SELECTOR}[open]`) !== null;
  document.documentElement.toggleAttribute(SCROLL_LOCK_ATTRIBUTE, hasOpenDialog && !isDesignMode());
  syncGestureGuard();
}

/** Rects a gesture may act on: every open dialog's box and its top-level content. */
function openDialogRects() {
  if (cachedDialogRects) return cachedDialogRects;

  const rects = [];
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    rects.push(...dialog.getClientRects());
    for (const child of dialog.children) rects.push(...child.getClientRects());
  });

  cachedDialogRects = rects;
  requestAnimationFrame(() => {
    cachedDialogRects = null;
  });
  return rects;
}

function isPointInOpenDialog(x, y) {
  return openDialogRects().some((rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom);
}

/** Whether a gesture belongs to a popup, latched at the press so a drag is never cut in half. */
function isGestureAllowed(event) {
  switch (event.type) {
    case 'touchstart': {
      const touch = event.touches[0] || event.changedTouches[0];
      gestureStartedInDialog = touch ? isPointInOpenDialog(touch.clientX, touch.clientY) : false;
      return gestureStartedInDialog;
    }
    case 'touchmove':
    case 'touchend':
    case 'touchcancel':
      return gestureStartedInDialog;
    case 'pointerdown':
    case 'mousedown':
      gestureStartedInDialog = isPointInOpenDialog(event.clientX, event.clientY);
      return gestureStartedInDialog;
    case 'pointermove':
    case 'mousemove':
      return event.buttons ? gestureStartedInDialog : isPointInOpenDialog(event.clientX, event.clientY);
    case 'pointerup':
    case 'pointercancel':
    case 'mouseup':
      return gestureStartedInDialog || isPointInOpenDialog(event.clientX, event.clientY);
    default:
      return isPointInOpenDialog(event.clientX, event.clientY);
  }
}

function handleGuardedGesture(event) {
  if (isGestureAllowed(event)) return;

  // Cancelling the default only stops native scrolling; a carousel translates
  // slides from its own listener, so the event must not reach the page at all.
  event.stopPropagation();
  if (CANCELLED_GESTURES.has(event.type) && event.cancelable) event.preventDefault();
}

/** Withhold every pointer, touch and wheel gesture aimed outside the open popups. */
function setGestureGuard(active) {
  if (active === gestureGuardActive) return;
  gestureGuardActive = active;
  cachedDialogRects = null;
  gestureStartedInDialog = false;

  if (!active) {
    GUARDED_GESTURES.forEach((type) => {
      document.removeEventListener(type, handleGuardedGesture, true);
    });
    return;
  }

  GUARDED_GESTURES.forEach((type) => {
    document.addEventListener(type, handleGuardedGesture, { capture: true, passive: false });
  });
}

function syncGestureGuard() {
  setGestureGuard(!isDesignMode() && document.querySelector('dialog[open]') !== null);
}

function syncTriggers(dialog, expanded) {
  if (!dialog.id) return;
  document
    .querySelectorAll(`[data-dialog-trigger][aria-controls="${CSS.escape(dialog.id)}"]`)
    .forEach((trigger) => trigger.setAttribute('aria-expanded', String(expanded)));
}

function focusInitialTarget(dialog) {
  // Opening already honors an explicit autofocus target.
  if (dialog.querySelector('[autofocus]')) return;
  const closeControl = dialog.querySelector(CLOSE_SELECTOR);
  if (closeControl instanceof HTMLElement) closeControl.focus({ preventScroll: true });
}

function focusableElements(dialog) {
  return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      element instanceof HTMLElement &&
      element.tabIndex >= 0 &&
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      element.getClientRects().length > 0
  );
}

/**
 * Open a managed dialog.
 *
 * @param {HTMLDialogElement} dialog - The `<dialog data-dialog>` to open.
 * @param {{opener?: Element}} [options] - `opener` receives focus again on close.
 * @returns {boolean} Whether this call opened the dialog.
 */
export function openDialog(dialog, { opener } = {}) {
  if (!isManagedDialog(dialog) || dialog.open || !dialog.isConnected) return false;

  if (opener instanceof HTMLElement) openerByDialog.set(dialog, opener);
  else openerByDialog.delete(dialog);
  skipFocusRestore.delete(dialog);

  if (isDesignMode()) {
    dialog.setAttribute(INLINE_ATTRIBUTE, '');
    dialog.show();
  } else {
    dialog.showModal();
  }

  updateScrollLock();
  syncTriggers(dialog, true);
  focusInitialTarget(dialog);

  dialog.dispatchEvent(new CustomEvent('yise:dialog:open', { bubbles: true }));
  return true;
}

/**
 * Close a managed dialog.
 *
 * @param {HTMLDialogElement} dialog - The `<dialog data-dialog>` to close.
 * @param {{restoreFocus?: boolean}} [options] - Set `restoreFocus` false when the
 *   opener is going away, so focus is not pulled to a detached element.
 * @returns {boolean} Whether this call closed the dialog.
 */
export function closeDialog(dialog, { restoreFocus = true } = {}) {
  if (!isManagedDialog(dialog) || !dialog.open) return false;
  if (!restoreFocus) skipFocusRestore.add(dialog);
  dialog.close();
  return true;
}

/**
 * The managed dialog a same-document fragment names.
 *
 * @param {string} hash - Fragment, including the leading `#`.
 * @returns {HTMLDialogElement|null} Dialog the fragment names, or `null` when it names something else.
 */
function dialogFromHash(hash) {
  if (!hash || hash.length < 2) return null;

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    id = hash.slice(1);
  }

  const dialog = document.getElementById(id);
  return isManagedDialog(dialog) ? dialog : null;
}

/**
 * The managed dialog a link opens instead of navigating to.
 *
 * @param {Element} link - Candidate anchor element.
 * @returns {HTMLDialogElement|null} Dialog the link names, or `null` when the link leaves this document.
 */
function dialogFromLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return null;

  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== window.location.pathname) return null;
  if (url.search !== window.location.search) return null;

  return dialogFromHash(url.hash);
}

function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const closeControl = event.target.closest(CLOSE_SELECTOR);
  if (closeControl) {
    const dialog = closeControl.closest(DIALOG_SELECTOR);
    if (isManagedDialog(dialog)) {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }
  }

  const trigger = event.target.closest(TRIGGER_SELECTOR);
  if (trigger) {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (isManagedDialog(dialog)) {
      event.preventDefault();
      openDialog(dialog, { opener: trigger });
    }
    return;
  }

  const isPlainActivation = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  if (isPlainActivation) {
    const link = event.target.closest('a[href*="#"]');
    const linkedDialog = dialogFromLink(link);
    if (linkedDialog) {
      event.preventDefault();
      openDialog(linkedDialog, { opener: link });
      return;
    }
  }

  // A click on `::backdrop` retargets to the dialog element itself.
  if (isManagedDialog(event.target) && event.target.open) closeDialog(event.target);
}

function handleClose(event) {
  const dialog = event.target;
  if (!isManagedDialog(dialog)) return;

  dialog.removeAttribute(INLINE_ATTRIBUTE);
  updateScrollLock();
  syncTriggers(dialog, false);

  const opener = openerByDialog.get(dialog);
  openerByDialog.delete(dialog);
  const restoreFocus = !skipFocusRestore.has(dialog);
  skipFocusRestore.delete(dialog);

  if (restoreFocus && opener instanceof HTMLElement && opener.isConnected) {
    opener.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (!dialog.open && opener.isConnected && document.activeElement !== opener) {
        opener.focus({ preventScroll: true });
      }
    });
  }
}

function handleKeydown(event) {
  if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

  const eventDialog = event.target instanceof Element ? event.target.closest(DIALOG_SELECTOR) : null;
  const dialog = isManagedDialog(eventDialog) ? eventDialog : document.querySelector(`${DIALOG_SELECTOR}[open]`);
  if (!isManagedDialog(dialog) || !dialog.open) return;

  const focusable = focusableElements(dialog);
  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus({ preventScroll: true });
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey ? active !== first : active !== last) return;

  event.preventDefault();
  (event.shiftKey ? last : first).focus({ preventScroll: true });
}

function followInitialFragment() {
  const dialog = dialogFromHash(window.location.hash);
  if (dialog) openDialog(dialog);
}

if (!window.YiseDialog) {
  document.addEventListener('click', handleClick);
  document.addEventListener('close', handleClose, true);
  document.addEventListener('keydown', handleKeydown, true);
  window.addEventListener('hashchange', followInitialFragment);

  // A section or block torn out while its dialog is open never fires `close`.
  document.addEventListener('shopify:section:unload', updateScrollLock);
  document.addEventListener('shopify:block:unload', updateScrollLock);

  // Drawers and lightboxes this controller does not own still open a dialog.
  new MutationObserver(syncGestureGuard).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open'],
  });
  syncGestureGuard();
  followInitialFragment();

  // Owners rendered inside a classic `{% javascript %}` tag cannot import.
  window.YiseDialog = { openDialog, closeDialog };
}
